import { shouldShowBackorderNotice } from "@theme/backorder";
import { setAddToCartButtonState } from "@theme/add-to-cart-state";

/**
 * Oversell Cap Enforcement for Shopify Horizon Theme
 *
 * Enforces oversell limits based on variant metafield custom.max_oversell_quantity
 * Effective max in cart: maxAllowed = max(0, inventory_quantity + max_oversell)
 *
 * Key points:
 * - Enforces on fetch() calls to /cart/add.js, /cart/change.js, /cart/update.js
 * - ALSO enforces on traditional form POSTs to /cart/add (capture phase)
 * - Updates quantity selectors:
 *    - Product page: max = remaining
 *    - Cart/drawer:  max = maxAllowed
 */

class OversellCapEnforcement {
  constructor() {
    this.config = {};
    this.cartCache = null;
    this.cartCacheTime = 0;
    this.CACHE_TTL = 500;

    // Units already committed to /cart/add.js but not yet reflected in the cart,
    // keyed by variant id. Without this every click of a spammed "Add to cart"
    // reads the same pre-click cart and they all pass the check.
    this.pendingAdds = new Map();

    this.init();
  }

  init() {
    this.loadConfig();
    this.interceptFetch();
    this.interceptAddToCartFormSubmits();
    this.observeDOM();
    this.observeQuantityChanges();

    window.OversellCap = {
      getConfig: (variantId) => this.config[String(variantId)],
      getMaxAllowed: (variantId) => this.computeMaxAllowed(variantId),
      getVariantInfo: (variantId) => this.getVariantInfo(variantId),
      getCart: () => this.getCart(),
      _instance: this,
    };

    console.log("[OversellCap] Initialized with config:", this.config);
  }

  loadConfig() {
    // Support your current id="oversell-config-{{ section.id }}"
    const scripts = document.querySelectorAll(
      'script[type="application/json"][id^="oversell-config-"]'
    );

    scripts.forEach((script) => {
      try {
        const data = JSON.parse(script.textContent || "{}");
        this.config = { ...this.config, ...data };
      } catch (e) {
        console.error("[OversellCap] Failed to parse config:", e);
      }
    });
  }

  async getCart() {
    const now = Date.now();
    if (this.cartCache && now - this.cartCacheTime < this.CACHE_TTL) {
      return this.cartCache;
    }
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      if (!res.ok) return null;
      this.cartCache = await res.json();
      this.cartCacheTime = now;
      return this.cartCache;
    } catch (e) {
      console.error("[OversellCap] Failed to fetch cart:", e);
      return null;
    }
  }

  getVariantInfo(variantId) {
    const id = String(variantId);
    const cfg = this.config[id];
    if (!cfg) return null;

    return {
      id,
      inventory_quantity: Number(cfg.inventory_quantity ?? 0),
      inventory_policy: String(cfg.inventory_policy ?? "deny"),
      inventory_managed: cfg.inventory_managed === true,
      max_oversell: Number(cfg.max_oversell ?? 0),
      backorder_message: cfg.backorder_message || "",
    };
  }

  getQuantityInCart(cart, variantId) {
    if (!cart?.items?.length) return 0;
    const id = String(variantId);
    const item = cart.items.find((i) => String(i.variant_id) === id);
    return item ? Number(item.quantity) : 0;
  }

  async computeMaxAllowed(variantId) {
    const id = String(variantId);
    const info = this.getVariantInfo(id);
    if (!info) return null;

    const policy = (info.inventory_policy || "").toLowerCase();
    const inventory = Number(info.inventory_quantity);
    const maxOversell = Number(info.max_oversell);

    let maxAllowed;

    if (policy === "continue") {
      // No oversell config -> made-to-order, oversells without limit.
      if (!Number.isFinite(maxOversell) || maxOversell <= 0) return null;

      // CRITICAL: allow negative inventory to reduce the allowed oversell window
      maxAllowed = Math.max(0, inventory + maxOversell);
    } else if (policy === "deny" && info.inventory_managed) {
      // A denied variant can never exceed its stock — and Shopify does not
      // enforce that on /cart/add.js (verified: adding 999 of a 7-stock variant
      // returns 200 and leaves 999 in the cart), so the cap has to live here.
      // Only tracked variants: untracked ones report 0 while selling freely.
      if (!Number.isFinite(inventory)) return null;

      maxAllowed = Math.max(0, inventory);
    } else {
      return null;
    }

    const cart = await this.getCart();
    const inCart = this.getQuantityInCart(cart, id);

    return {
      maxAllowed,
      inventory,
      maxOversell,
      inCart,
      remaining: Math.max(0, maxAllowed - inCart),
    };
  }

  // ----------------------------
  // Enforcement hooks
  // ----------------------------

  isCartEndpoint(url) {
    if (!url) return false;
    const u = String(url);
    return (
      u.includes("/cart/add") ||
      u.includes("/cart/change") ||
      u.includes("/cart/update")
    );
  }

  interceptFetch() {
    const originalFetch = window.fetch;

    window.fetch = async (input, init = {}) => {
      const url = typeof input === "string" ? input : input?.url;

      if (!this.isCartEndpoint(url)) return originalFetch(input, init);

      try {
        const method = (init.method || "GET").toUpperCase();

        if (url.includes("/cart/add") && (method === "POST" || method === "PUT")) {
          return await this.enforceCartAdd(originalFetch, input, init);
        }

        if (
          (url.includes("/cart/change") || url.includes("/cart/update")) &&
          (method === "POST" || method === "PUT")
        ) {
          return await this.enforceCartChange(originalFetch, input, init);
        }

        return originalFetch(input, init);
      } catch (e) {
        console.error("[OversellCap] Enforcement error:", e);
        return originalFetch(input, init);
      }
    };
  }

  parseBody(body) {
    if (!body) return {};

    if (body instanceof FormData) {
      const obj = {};
      for (const [k, v] of body.entries()) obj[k] = v;
      return obj;
    }

    if (body instanceof URLSearchParams) {
      const obj = {};
      for (const [k, v] of body.entries()) obj[k] = v;
      return obj;
    }

    if (typeof body === "string") {
      try {
        if (body.trim().startsWith("{")) return JSON.parse(body);
        const params = new URLSearchParams(body);
        const obj = {};
        for (const [k, v] of params.entries()) obj[k] = v;
        return obj;
      } catch {}
    }

    if (typeof body === "object") return body;
    return {};
  }

  rewriteBody(body, updates) {
    if (body instanceof FormData) {
      Object.entries(updates).forEach(([k, v]) => body.set(k, String(v)));
      return body;
    }

    if (body instanceof URLSearchParams) {
      Object.entries(updates).forEach(([k, v]) => body.set(k, String(v)));
      return body.toString();
    }

    if (typeof body === "string") {
      try {
        if (body.trim().startsWith("{")) {
          const obj = JSON.parse(body);
          return JSON.stringify({ ...obj, ...updates });
        }
        const params = new URLSearchParams(body);
        Object.entries(updates).forEach(([k, v]) => params.set(k, String(v)));
        return params.toString();
      } catch {}
    }

    if (typeof body === "object") {
      return JSON.stringify({ ...body, ...updates });
    }

    return body;
  }

  createErrorResponse(status, payload) {
    return new Response(JSON.stringify(payload), {
      status,
      headers: { "Content-Type": "application/json" },
    });
  }

  showToast(message) {
    let toast = document.getElementById("oversell-toast");
    if (!toast) {
      toast = document.createElement("div");
      toast.id = "oversell-toast";
      toast.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 999999;
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        opacity: 0;
        transition: opacity 0.2s;
        max-width: 90vw;
        text-align: center;
      `;
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    requestAnimationFrame(() => (toast.style.opacity = "1"));
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => (toast.style.opacity = "0"), 2500);
  }

  async enforceCartAdd(originalFetch, input, init) {
    const data = this.parseBody(init.body);
    const variantId = data.id;
    const requestedQty = Number(data.quantity || 1);

    if (!variantId || requestedQty <= 0) return originalFetch(input, init);

    const limits = await this.computeMaxAllowed(variantId);
    if (!limits) return originalFetch(input, init);

    const { maxAllowed, inCart } = limits;

    // Everything reserved by adds still in flight is already spoken for. This
    // read happens synchronously after the await above, so concurrent clicks
    // resume one at a time and each sees what the previous one reserved.
    const id = String(variantId);
    const pending = this.pendingAdds.get(id) ?? 0;
    const remaining = Math.max(0, limits.remaining - pending);

    if (remaining === 0) {
      this.showToast("This item is sold out.");
      return this.createErrorResponse(422, {
        status: 422,
        message: "Sold out",
        description: `You already have ${inCart + pending} in cart (max allowed: ${maxAllowed}).`,
      });
    }

    const grantedQty = Math.min(requestedQty, remaining);

    if (grantedQty < requestedQty) {
      init.body = this.rewriteBody(init.body, { quantity: grantedQty });
      this.showToast(`Adjusted to ${grantedQty} (max available).`);
    }

    this.pendingAdds.set(id, pending + grantedQty);

    try {
      return await originalFetch(input, init);
    } finally {
      // The units are in the cart now (or the add failed) — either way the
      // reservation is over and the cached cart is stale.
      this.pendingAdds.set(id, Math.max(0, (this.pendingAdds.get(id) ?? 0) - grantedQty));
      this.cartCache = null;
    }
  }

  async enforceCartChange(originalFetch, input, init) {
    const data = this.parseBody(init.body);
    const requestedQty = Number(data.quantity);

    if (!Number.isFinite(requestedQty)) return originalFetch(input, init);

    const cart = await this.getCart();
    if (!cart?.items?.length) return originalFetch(input, init);

    let item = null;
    if (data.id) item = cart.items.find((i) => String(i.variant_id) === String(data.id));
    else if (data.line) item = cart.items[Number(data.line) - 1];
    else if (data.key) item = cart.items.find((i) => String(i.key) === String(data.key));

    if (!item) return originalFetch(input, init);

    const limits = await this.computeMaxAllowed(item.variant_id);
    if (!limits) return originalFetch(input, init);

    const { maxAllowed } = limits;

    if (requestedQty > maxAllowed) {
      init.body = this.rewriteBody(init.body, { quantity: maxAllowed });
      this.showToast(`Maximum quantity is ${maxAllowed}.`);
    }

    return originalFetch(input, init);
  }

  /**
   * NEW: Enforce when Horizon submits a real form to /cart/add
   * This is the missing piece causing your Test 1 failure mode.
   */
  interceptAddToCartFormSubmits() {
    document.addEventListener(
      "submit",
      async (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;

        const action = (form.getAttribute("action") || "").toLowerCase();
        if (!action.includes("/cart/add")) return;

        const idEl = form.querySelector('input[name="id"]');
        if (!idEl?.value) return;

        const qtyEl =
          form.querySelector('input[name="quantity"]') ||
          form.querySelector('input[type="number"]');

        const variantId = idEl.value;
        const requestedQty = Number(qtyEl?.value || 1);

        const limits = await this.computeMaxAllowed(variantId);
        if (!limits) return;

        const { remaining } = limits;

        if (remaining === 0) {
          e.preventDefault();
          e.stopPropagation();
          this.showToast("This item is sold out.");
          this.updateQuantitySelectors(); // refresh UI
          return;
        }

        if (requestedQty > remaining) {
          // Clamp and allow submit
          if (qtyEl) qtyEl.value = String(remaining);
          this.showToast(`Adjusted to ${remaining} (max available).`);
        }
      },
      true // capture: must run before theme handlers
    );
  }

  // ----------------------------
  // UI updates
  // ----------------------------

  observeDOM() {
    // Debounced so our own DOM writes (which the MutationObserver sees) don't
    // trigger an unbounded re-run loop.
    this.scheduleRun = this.debounce(() => this.updateQuantitySelectors(), 50);

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.scheduleRun());
    } else {
      this.scheduleRun();
    }

    // Horizon dispatches these (see @theme/events ThemeEvents). The previous
    // 'cart:updated' name never fired, so cart changes left the cache stale
    // and the product page stuck on "Sold out" until reload.
    ["cart:update", "variant:update", "quantity-selector:update"].forEach(
      (evt) => {
        document.addEventListener(evt, () => {
          this.cartCache = null;
          this.scheduleRun();
        });
      }
    );

    const observer = new MutationObserver(() => this.scheduleRun());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  // Fallback for quantity changes in case Horizon's 'quantity-selector:update'
  // event isn't dispatched: stepper/input changes don't mutate the DOM tree,
  // so the MutationObserver alone would miss them.
  observeQuantityChanges() {
    const update = () => this.scheduleRun();
    const isQtyInput = (el) =>
      el?.matches?.('quantity-selector-component input[type="number"]');

    document.addEventListener("input", (e) => {
      if (isQtyInput(e.target)) update();
    });
    document.addEventListener("change", (e) => {
      if (isQtyInput(e.target)) update();
    });
    document.addEventListener("click", (e) => {
      if (e.target?.closest?.("quantity-selector-component button")) {
        update();
      }
    });
  }

  setATCDisabled(disabled, label) {
    // One selector, not two concatenated lists: the theme's button carries both
    // `name="add"` and `.add-to-cart-button`, so concatenating processed it twice.
    const buttons = document.querySelectorAll(
      'button[name="add"], .add-to-cart-button'
    );

    buttons.forEach((btn) => {
      setAddToCartButtonState(btn, { disabled, label });
    });
  }

  async updateQuantitySelectors() {
    const selectors = document.querySelectorAll(
      "quantity-selector-component, cart-quantity-selector-component"
    );

    for (const selector of selectors) {
      try {
        const input = selector.querySelector('input[type="number"]');
        if (!input) continue;

        const variantId = selector.dataset.variantId || input.dataset.variantId;
        if (!variantId) continue;

        const limits = await this.computeMaxAllowed(variantId);
        if (!limits) continue;

        const { maxAllowed, remaining } = limits;

        const isCartSelector =
          selector.tagName.toLowerCase() === "cart-quantity-selector-component";

        // The `max` attribute is always the absolute maximum in cart.
        // Horizon's PDP quantity component already subtracts the in-cart
        // quantity (getEffectiveMax => max - cartQuantity), so using
        // `remaining` here would double-subtract it; the cart component uses
        // max directly. "remaining" is only for the sold-out decision below.
        input.setAttribute("max", String(maxAllowed));

        if (selector.updateConstraints) {
          const min = input.min || "1";
          const step = input.step || "1";
          selector.updateConstraints(min, String(maxAllowed), step);
        } else if (selector.updateButtonStates) {
          selector.updateButtonStates();
        }

        // If product page and nothing more can be added, disable ATC
        if (!isCartSelector && remaining === 0) {
          this.setATCDisabled(true, "Sold out");
        } else if (!isCartSelector) {
          // If add becomes possible again, re-enable
          this.setATCDisabled(false);
        }
      } catch (e) {
        console.error("[OversellCap] selector update failed:", e);
      }
    }

    await this.updateBackorderMessages();
  }

  /**
   * Toggle the product-page backorder message per product form.
   * Committed qty = quantity already in cart + current selector value.
   * Uses the shared shouldShowBackorderNotice rule so PDP and cart agree.
   */
  async updateBackorderMessages() {
    if (typeof shouldShowBackorderNotice !== "function") {
      if (!this._warnedNoEval) {
        console.error(
          "[OversellCap] shouldShowBackorderNotice unavailable — check the @theme/backorder importmap entry and that backorder.js is deployed."
        );
        this._warnedNoEval = true;
      }
      return;
    }

    const forms = document.querySelectorAll("product-form-component");

    for (const form of forms) {
      const msgEl = form.querySelector(".backorder-message");
      if (!msgEl) continue;

      const textEl = msgEl.querySelector(".backorder-message__text");
      if (textEl && msgEl.dataset.defaultText == null) {
        msgEl.dataset.defaultText = textEl.textContent.trim();
      }

      const variantId = form.querySelector('input[name="id"]')?.value;
      const info = variantId ? this.getVariantInfo(variantId) : null;

      let shouldShow = false;
      if (info) {
        const qtyInput = form.querySelector(
          'quantity-selector-component input[type="number"]'
        );
        const selectorQty = Number(qtyInput?.value) || 1;
        const cart = await this.getCart();
        const inCart = this.getQuantityInCart(cart, variantId);

        // `remaining` is what still fits in the cart. When it hits 0 the ATC
        // flips to "Sold out" (see updateQuantitySelectors), so the shared rule
        // suppresses the notice to keep the message and the button in agreement.
        const limits = await this.computeMaxAllowed(variantId);

        shouldShow = shouldShowBackorderNotice({
          inventoryPolicy: info.inventory_policy,
          maxOversell: info.max_oversell,
          effectiveInventory: info.inventory_quantity,
          committedQty: inCart + selectorQty,
          remaining: limits ? limits.remaining : Infinity,
        });
      }

      // Only mutate the DOM when something actually changes — writing
      // textContent every run would re-trigger the MutationObserver.
      if (shouldShow && textEl) {
        const nextText =
          (info && info.backorder_message) || msgEl.dataset.defaultText || "";
        if (nextText && textEl.textContent !== nextText) {
          textEl.textContent = nextText;
        }
      }

      const shouldHide = !shouldShow;
      if (msgEl.hidden !== shouldHide) {
        msgEl.hidden = shouldHide;
      }
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new OversellCapEnforcement());
} else {
  new OversellCapEnforcement();
}

export default OversellCapEnforcement;
